// ====================================================================
// — 「弹窗点遮罩不关闭」(modal backdrop click)
// (辉哥 2026-10-24 22:0x 拍板 B 方案: 改 Flarum core 基类默认行为)
//
// ────────────────────────────────────────────────────────────────────
// 目标
//   全站弹窗默认**只**能靠「右上角关闭按钮」或「Esc 键」关闭,
//   点弹窗后面的遮罩层**不再**关闭。
//
// ────────────────────────────────────────────────────────────────────
// Flarum 2.0 机制 (vendor 原文, 勿凭印象改)
// ────────────────────────────────────────────────────────────────────
//   1. 可见的那层暗色遮罩 `.Modal-backdrop` (less/common/Modal.less:22-39)
//      **根本没有 onclick**, 只负责变暗 + 动画。
//   2. 真正接「点外面关闭」的是一层**不可见**的全屏层:
//        vendor/flarum/core/js/src/common/components/ModalManager.tsx:63-64
//          /* This backdrop is invisible and used for outside clicks to close the modal. */
//          <div className="ModalManager-invisibleBackdrop"
//                onclick={this.handlePossibleBackdropClick.bind(this)} />
//   3. 它的点击处理 (同文件 :235-239):
//          if (!modal || !modal.componentClass.dismissibleOptions.viaBackdropClick) return;
//          this.animateHide();
//      ⇒ **有公开开关**: viaBackdropClick
//   4. 开关来自基类 (common/components/Modal.tsx:37-53):
//          protected static readonly isDismissibleViaCloseButton  = true;
//          protected static readonly isDismissibleViaEscKey       = true;
//          protected static readonly isDismissibleViaBackdropClick = true;
//          static get dismissibleOptions() { return {viaCloseButton, viaEscKey, viaBackdropClick} }
//      ModalManager 在**点击那一刻**读 `modal.componentClass.dismissibleOptions`
//      ⇒ 运行时改就生效, 不受加载时机影响。
//
// ────────────────────────────────────────────────────────────────────
// 本补丁怎么改 (为什么不是直接改基类静态字段)
// ────────────────────────────────────────────────────────────────────
//   直接写 `Modal.isDismissibleViaBackdropClick = false` 也能生效, 但有两个问题:
//     ① **读不到 setting**。app.forum 在 Application.boot() 里是
//        `initialize()` **跑完之后**才赋值 (common/Application.tsx:378-382)
//        ⇒ 在 app.initializers 里读 app.forum 永远是 undefined
//        ⇒ 必须在**点击那一刻**惰性读。
//     ② **子类显式表态会被吞**。全站 243 个弹窗里:
//          - 94 个显式写了 `isDismissibleViaBackdropClick = false` (本来就不可点遮罩关)
//          - 1 个 (ziven-bjxy-website 的 GroupPickerModal) 显式写了 `= true`
//            (作者**明确要**能点遮罩关)
//        ⇒ 一刀切会把那 1 个的显式意图也改掉。
//
//   ⇒ 所以本补丁**包装基类的静态 getter**:
//       · 子类**自己声明过** isDismissibleViaBackdropClick → 尊重它 (不碰)
//       · 子类**没声明** (= 走默认) → 返回 viaBackdropClick: false
//       · setting 切成「允许」→ 原样返回 (恢复 Flarum 原始行为)
//     语义 = 辉哥原话「**默认**改为只点关闭按钮才关」。
//
// ────────────────────────────────────────────────────────────────────
// 两个 bundle 各有一份基类 (重要)
// ────────────────────────────────────────────────────────────────────
//   forum 栈与 admin 栈是**两次独立编译**, 各带一份 Modal 基类副本
//   (生产实测: public/assets/forum.js 与 admin.js 各有一份,
//   8 个扩展分块里都是 0 份副本)
//   ⇒ 本模块必须被 **forum/index.js 与 admin/index.js 各 import 一次**,
//     各自 bundle 各装一次、各自改各自那份 ⇒ **改一处要同步另一处**。
//
// ────────────────────────────────────────────────────────────────────
// setting 口径
// ────────────────────────────────────────────────────────────────────
//   后端 setting: ziven-core.modal_backdrop_close
//     '0' (默认) = 不允许点遮罩关闭 (新行为)
//     '1'         = 允许点遮罩关闭 (Flarum 原始行为, 应急回退开关)
//   前端读 forum attribute: zivenCoreModalAllowBackdropClose (bool)
//
// ────────────────────────────────────────────────────────────────────
// 影响面
// ────────────────────────────────────────────────────────────────────
//   · 只影响 **≥768px 的桌面/平板**: 手机端弹窗铺满全屏 (Modal.less @phone
//     .Modal-content{min-height:100dvh}), **根本没有遮罩可点**。
//   · Esc 键**保留** (viaEscKey 不动) —— 键盘可达性 + WAI-ARIA 对话框规范建议。
//   · 7 个「没有关闭按钮」的弹窗 (AccountExpired / AccountNeedActivate /
//     pdfGenerating / AccountBanned / 3 个 PostReward*) **全部自带**
//     `isDismissibleViaBackdropClick = false` ⇒ 本补丁碰不到它们
//     ⇒ 不会把任何用户困在弹窗里。
// ====================================================================

import Modal from 'flarum/common/components/Modal';

// forum attribute 名 (对应后端 Schema\Boolean::make('zivenCoreModalAllowBackdropClose'))
const ATTR_ALLOW_BACKDROP_CLOSE = 'zivenCoreModalAllowBackdropClose';
export function installModalBackdropClose(app) {
  'use strict';

  try {
    if (!Modal) {
      console.warn('[ziven-core] modalBackdropClose: Modal 模块不可用, 跳过安装。');
      return false;
    }
    const desc = Object.getOwnPropertyDescriptor(Modal, 'dismissibleOptions');
    if (!desc || typeof desc.get !== 'function') {
      console.warn('[ziven-core] modalBackdropClose: 基类没有 dismissibleOptions getter, 跳过安装。');
      return false;
    }

    // 幂等: 同一 bundle 被重复 import / 两个入口都装了 → 只装一次
    if (desc.get.__zivenCoreBackdropPatched) {
      return true;
    }
    const originalGet = desc.get;
    const patchedGet = function () {
      const opts = originalGet.call(this);

      // ① 子类自己声明过 → 尊重它的显式表态 (94 个 false / 1 个 true)
      if (Object.prototype.hasOwnProperty.call(this, 'isDismissibleViaBackdropClick')) {
        return opts;
      }

      // ② 本来就不可点 → 原样
      if (opts.viaBackdropClick === false) {
        return opts;
      }

      // ③ 走默认值 → 按 setting 决定 (惰性读, 因为 app.forum 在
      //    initializers 阶段还没赋值, Application.tsx:378-382)
      let allow = false;
      try {
        allow = !!(app && app.forum && app.forum.attribute(ATTR_ALLOW_BACKDROP_CLOSE));
      } catch (e) {
        allow = false;
      }
      if (allow) {
        return opts; // 后台切回「允许」→ 恢复 Flarum 原始行为
      }
      return Object.assign({}, opts, {
        viaBackdropClick: false
      });
    };
    patchedGet.__zivenCoreBackdropPatched = true;
    Object.defineProperty(Modal, 'dismissibleOptions', {
      configurable: true,
      get: patchedGet
    });
    return true;
  } catch (e) {
    console.warn('[ziven-core] modalBackdropClose 安装失败:', e && e.message);
    return false;
  }
}
export default installModalBackdropClose;
flarum.reg.add('ziiven-ziven-core', 'common/utils/modalBackdropClose', installModalBackdropClose);